import React, { useState, useEffect } from 'react';
import {
  Calendar as CalendarIcon,
  MapPin,
  Clock,
  Search,
  Filter,
  Check,
  X,
  Camera,
  Video,
  Ban,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import ProjectDetailsModal from '../components/requirements/ProjectDetailsModal';
import { getProject as getProjectDetails } from '../lib/api/requirementsApi';
import {
  getDashboardCount,
  getStatusCount,
  getPendingProjects,
  GetUpcomingShoots,
  acceptOrDeclineProject
} from '../services/creatorService';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';

const cardHoverClass =
  "bg-white dark:bg-card p-5 rounded-xl border border-[#E8DDD3] dark:border-border shadow-sm " +
  "transition-all duration-300 ease-in-out " +
  "hover:border-[#e8d0aa] dark:hover:border-primary/50 hover:shadow-[4px_4px_0px_0px_#e8d0aa] dark:hover:shadow-[4px_4px_0px_0px_rgba(232,208,170,0.2)] hover:-translate-y+1 " +
  "hover:scale-[1.01] cursor-pointer relative overflow-hidden flex flex-col";

const statCardClass =
  "bg-white dark:bg-card p-4 md:p-6 rounded-xl shadow-sm border border-gray-100 dark:border-border flex items-start justify-between relative overflow-hidden group " +
  "hover:shadow-md hover:border-[#C4B5A3] dark:hover:border-primary/50 transition-all duration-300 " +
  "after:content-[''] after:absolute after:-top-[150%] after:-left-[150%] after:w-[300%] after:h-[300%] " +
  "after:bg-gradient-to-br after:from-transparent after:via-white/40 dark:after:via-white/5 after:to-transparent " +
  "after:rotate-12 group-hover:after:top-[120%] group-hover:after:left-[120%] after:transition-all after:duration-500 after:ease-in-out";

const StatusBadge = ({ status }) => {
  const map = {
    Confirmed: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50",
    Pending: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800/50"
  };
  return <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${map[status] || "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"}`}>{status}</span>;
};

const RequestsShootsPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [projectDetailsOpen, setProjectDetailsOpen] = useState(false);
  const [projectDetailsData, setProjectDetailsData] = useState(null);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('shoot_request');
  const [statusFilter, setStatusFilter] = useState('all');
  const [date, setDate] = useState('today');
  const [acceptShootEvent, setAcceptShootEvent] = useState(null);
  const [declineShootEvent, setDeclineShootEvent] = useState(null);
  const [declineReason, setDeclineReason] = useState('Schedule conflict');
  const [declineComments, setDeclineComments] = useState('');

  // Data states
  const [crewMemberId, setCrewMemberId] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Stats definition derived from dynamic data
  const stats = [
    {
      title: 'Pending Shoots',
      value: dashboardStats?.pendingRequests || 0,
      icon: Clock
    },
    {
      title: 'Confirmed Shoots',
      value: dashboardStats?.confirmedRequests || 0,
      icon: Camera
    },
    {
      title: 'Completed Shoots',
      value: dashboardStats?.completedShoots || 0,
      icon: Video
    },
    {
      title: 'Declined',
      value: dashboardStats?.declinedRequests || 0,
      icon: Ban
    },
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const id = user?.crew_member_id;
    if (id) {
      setCrewMemberId(id);
    }
  }, []);

  useEffect(() => {
    if (!crewMemberId) return;

    fetchData();
  }, [crewMemberId]);

  const fetchData = async () => {
    if (!crewMemberId) return;
    setIsLoading(true);

    const getCleanLocation = (loc) => {
      if (!loc || loc === 'Location TBD') return 'Location TBD';

      let addressStr = loc;

      try {
        if (typeof loc === 'string' && loc.trim().startsWith('{')) {
          const parsed = JSON.parse(loc);
          addressStr = parsed.address || addressStr;
        } else if (typeof loc === 'object' && loc !== null) {
          addressStr = loc.address || addressStr;
        }
      } catch (e) {
        console.error("Location parse error", e);
      }

      if (addressStr.includes(',')) {
        const parts = addressStr.split(',').map(p => p.trim());
        if (parts.length >= 3) {
          const city = parts[1];
          const state = parts[2].replace(/[0-9]/g, '').trim();
          return `${city}, ${state}`;
        }
        return parts[0];
      }

      return addressStr;
    };

    try {
      // 1. Fetch Stats
      const statsResponse = await getStatusCount(crewMemberId);
      if (statsResponse.success) {
        setDashboardStats(statsResponse.data);
      }

      // 2. Fetch Projects (Pending and Upcoming)
      const [pendingRes, upcomingRes] = await Promise.all([
        getPendingProjects(crewMemberId),
        GetUpcomingShoots({ crew_member_id: crewMemberId })
      ]);

      let allProjects = [];

      if (pendingRes.success && Array.isArray(pendingRes.data)) {
        const pending = pendingRes.data.map(p => ({
          ...p,
          status: 'Pending',
          title: p.project_name || p.title || 'Untitled Project',
          date: p.event_date || p.shoot_date || p.start_date || 'TBD',
          location: getCleanLocation(p.event_location || p.location),
          time: (p.start_time && p.end_time) ? `${p.start_time} - ${p.end_time}` : (p.shoot_time || 'Time TBD'),
          project_id: p.project_id || p.id
        }));
        allProjects = [...allProjects, ...pending];
      }

      if (upcomingRes.success && Array.isArray(upcomingRes.data)) {
        const upcoming = upcomingRes.data.map(p => ({
          ...p,
          status: 'Confirmed',
          title: p.project_name || p.title || 'Untitled Project',
          date: p.event_date || p.shoot_date || p.start_date || 'TBD',
          location: getCleanLocation(p.event_location || p.location),
          time: (p.start_time && p.end_time) ? `${p.start_time} - ${p.end_time}` : (p.shoot_time || 'Time TBD'),
          project_id: p.project_id || p.id
        }));
        allProjects = [...allProjects, ...upcoming];
      }

      setProjects(allProjects);

    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptProject = async (projectId, accept) => {
    if (!crewMemberId) return;

    try {
      const response = await acceptOrDeclineProject(
        projectId,
        accept ? 1 : 2
      );

      if (response.success) {
        // Refresh project list
        await fetchData();

        // Success Toast
        if (accept) {
          toast.custom((t) => (
            <div
              className={`${t.visible ? 'animate-enter' : 'animate-leave'
                } max-w-md w-full bg-[#F0FDF4] dark:bg-emerald-950/30 border border-[#22C55E] dark:border-emerald-500/50 shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
            >
              <div className="flex-1 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-[#22C55E]" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1 pr-6">
                    <p className="text-base font-bold text-gray-900 dark:text-foreground">
                      Shoot request accepted
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-muted-foreground leading-relaxed">
                      You've successfully accepted the shoot. Check your calendar for details.
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="absolute top-4 right-4 text-gray-900 hover:text-gray-700 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ), { duration: 4000 });
        } else {
          toast.custom((t) => (
            <div
              className={`${t.visible ? 'animate-enter' : 'animate-leave'
                } max-w-md w-full bg-[#FEF2F2] dark:bg-red-950/30 border border-[#EF4444] dark:border-red-500/50 shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
            >
              <div className="flex-1 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1 pr-6">
                    <p className="text-base font-bold text-gray-900 dark:text-foreground">
                      Request Declined
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-muted-foreground leading-relaxed">
                      The shoot request has been declined.
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="absolute top-4 right-4 text-gray-900 hover:text-gray-700 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ), { duration: 4000 });
        }

        // Close modals
        setAcceptShootEvent(null);
        setDeclineShootEvent(null);
      } else {
        console.error('Failed to accept/decline project:', response.error);
        toast.error('Failed to process request. Please try again.');
      }
    } catch (err) {
      console.error('Error accepting/declining project:', err);
      toast.error('An error occurred. Please try again.');
    }
  };

  const filteredCards = projects.filter(c => {
    const matchesSearch = (c.title || '').toLowerCase().includes(search.trim().toLowerCase());
    const matchesStatus = statusFilter === 'all' ? true : c.status.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleOpenProjectDetails = async (projectId) => {
    try {
      const response = await getProjectDetails(projectId);
      if (response && response.data) {
        setProjectDetailsData(response.data);
        setProjectDetailsOpen(true);
      }
    } catch (e) {
      toast.error("Failed to load project details");
    }
  };

  return (
    <div className="p-0 md:p-6 bg-white dark:bg-background min-h-screen">
      <div className="bg-[#F5F1EE] dark:bg-secondary/10 md:rounded-2xl border border-[#E8DDD3] dark:border-border">
        <div className="p-4 md:p-6 pb-3 border-b border-[#E8DDD3] dark:border-border">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">
                Requests & Shoots
              </h1>
              <p className="text-gray-500 dark:text-muted-foreground text-sm mt-1">
                Manage your project requests and confirmed assignments
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
            {stats.map((s, idx) => (
              <div
                key={idx}
                className={`${statCardClass} relative overflow-hidden group `}
              >
                {/* AMBIENT LIGHT LAYER */}
                <div className="ambient-light" />

                {/* CONTENT */}
                <div className="relative z-10 flex justify-between items-start w-full">
                  <div className="md:pr-12">
                    <p className="text-gray-500 dark:text-muted-foreground text-sm font-medium transition-all duration-300 group-hover:text-base group-hover:leading-none group-hover:font-semibold leading-none h-7 flex items-center">
                      {s.title}
                    </p>
                    <h3 className="text-4xl font-bold text-gray-900 dark:text-foreground mt-2">
                      {s.value}
                    </h3>
                  </div>

                  <div className="hidden md:flex p-3 rounded-xl bg-[#F4F0ED] dark:bg-secondary/20 transition-all duration-300 group-hover:bg-[#B8A697] dark:group-hover:bg-primary/20 group-hover:scale-110">
                    <s.icon className="h-6 w-6 text-gray-700 dark:text-foreground transition-colors duration-300 group-hover:text-white dark:group-hover:text-primary" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900 dark:text-foreground">Pending Shoots</h2>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <div className="relative flex-1 w-full max-w-xs md:max-w-xl">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events or cre..."
                className="pl-10 bg-white dark:bg-background border-none shadow-sm h-10 text-gray-900 dark:text-foreground"
              />
            </div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-white dark:bg-background border-none shadow-sm h-10 w-[160px] text-gray-900 dark:text-foreground">
                <SelectValue placeholder="Shoot Request" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-card border-gray-100 dark:border-border">
                <SelectItem value="shoot_request">Shoot Request</SelectItem>
                <SelectItem value="equipment_request">
                  Equipment Request
                </SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-white dark:bg-background border-none shadow-sm h-10 w-[140px] text-gray-900 dark:text-foreground">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-card border-gray-100 dark:border-border">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              className="bg-white dark:bg-background border-none shadow-sm h-10 px-3 text-gray-500 dark:text-muted-foreground font-normal flex gap-2"
            >
              {date === "today" ? "Today" : "Date"}
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="p-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredCards.length > 0 ? (
              filteredCards.map((item) => (
                <div
                  key={item.id || item.project_id}
                  className={cardHoverClass}
                >
                  <div className="flex justify-between items-center mb-4">
                    <span
                      className={`${item.status === "Confirmed"
                        ? "bg-[#22C55E]"
                        : "bg-[#6366F1]"
                        } text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 uppercase tracking-wider`}
                    >
                      {item.status === "Confirmed" ? (
                        <CheckCircle2 className="h-3 w-3" />
                      ) : (
                        <Clock className="h-3 w-3" />
                      )}
                      {item.status === "Confirmed"
                        ? "Confirmed"
                        : "Shoot Request"}
                    </span>
                    <div className="flex -space-x-2 shrink-0">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="w-7 h-7 rounded-full border-2 border-white dark:border-card bg-gradient-to-br from-purple-400 to-pink-400 overflow-hidden flex items-center justify-center"
                        >
                          <img
                            src={`https://i.pravatar.cc/100?img=${10 + i}`}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                      <div className="w-7 h-7 rounded-full border-2 border-white dark:border-card bg-white dark:bg-background flex items-center justify-center text-[10px] font-medium text-gray-400 dark:text-muted-foreground shadow-sm">
                        +3
                      </div>
                    </div>
                  </div>

                  <h3 className="font-bold text-xl leading-tight text-gray-900 dark:text-foreground mb-4">
                    {item.title}
                  </h3>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-5 text-gray-400 dark:text-muted-foreground text-[13px]">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      <span>{item.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>{item.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>
                        {item.time === "Time TBD"
                          ? ""
                          : item.time}
                      </span>
                    </div>
                  </div>

                  <div className="mt-auto pt-4 border-t border-[#F1E9E2] dark:border-border flex items-center justify-between">
                    <span className="text-gray-400 dark:text-muted-foreground text-sm font-medium">
                      10 min ago
                    </span>
                    <div className="flex gap-2">
                      <Button
                        className="bg-gray-900 dark:bg-primary text-white dark:text-primary-foreground hover:bg-black dark:hover:bg-primary/90 rounded-xl px-5 h-10 text-sm font-semibold"
                        onClick={() =>
                          handleOpenProjectDetails(item.project_id)
                        }
                      >
                        View Details
                      </Button>
                      {item.status === "Pending" && (
                        <>
                          <Button
                            variant="outline"
                            className="bg-[#DCFCE7] dark:bg-emerald-900/30 border-none text-[#22C55E] dark:text-emerald-400 hover:bg-[#bbf7d0] dark:hover:bg-emerald-800/40 h-10 w-10 p-0 rounded-lg"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAcceptShootEvent(item);
                            }}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            className="bg-[#F8F8F8] dark:bg-secondary/20 border-none text-gray-900 dark:text-foreground hover:bg-gray-100 dark:hover:bg-gray-800 h-10 w-10 p-0 rounded-lg"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeclineShootEvent(item);
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-10 text-gray-500 dark:text-muted-foreground">
                {isLoading ? "Loading projects..." : "No projects found"}
              </div>
            )}
          </div>
        </div>
      </div>

      <ProjectDetailsModal
        open={projectDetailsOpen}
        onOpenChange={setProjectDetailsOpen}
        project={projectDetailsData}
      />

      {/* Accept Shoot Modal */}
      <Dialog
        open={!!acceptShootEvent}
        onOpenChange={() => setAcceptShootEvent(null)}
      >
        <DialogContent className="fixed z-[100] left-[50%] top-[50%] w-full max-w-md translate-x-[-50%] translate-y-[-50%] p-6 bg-white dark:bg-card shadow-xl rounded-xl border border-gray-100 dark:border-border">
          <div className="flex flex-col items-center text-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-foreground mb-2">
              Are you sure you want to accept this shoot?
            </h2>
            <p className="text-gray-500 dark:text-muted-foreground text-sm mb-6">
              This will add it to your calendar and notify the production team
              of your confirmation.
            </p>
            <div className="flex gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1 bg-gray-100 dark:bg-background border-none text-gray-900 dark:text-foreground hover:bg-gray-200 dark:hover:bg-gray-800"
                onClick={() => setAcceptShootEvent(null)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                onClick={() => {
                  if (acceptShootEvent) {
                    handleAcceptProject(
                      acceptShootEvent.project_id || acceptShootEvent.id,
                      true
                    );
                  }
                }}
              >
                <Check className="h-4 w-4" /> Accept Shoot
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Decline Shoot Modal */}
      <Dialog
        open={!!declineShootEvent}
        onOpenChange={() => setDeclineShootEvent(null)}
      >
        <DialogContent className="fixed z-[100] left-[50%] top-[50%] w-full max-w-md translate-x-[-50%] translate-y-[-50%] p-6 bg-white dark:bg-card shadow-xl rounded-xl border border-gray-100 dark:border-border">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-foreground">
                  Decline Shoot Request?
                </h2>
              </div>
            </div>
            {/* <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400" onClick={() => setDeclineShootEvent(null)}>
              <X className="h-4 w-4" />
            </Button> */}
          </div>

          <p className="text-gray-500 dark:text-muted-foreground text-sm mb-6">
            Please let us know why you're declining this request. This helps
            improve future matching.
          </p>

          <div className="mb-6">
            <h3 className="font-bold text-gray-900 dark:text-foreground mb-3 block">
              Reason for declining
            </h3>
            <div className="space-y-3">
              {[
                "Schedule conflict",
                "Equipment unavailable",
                "Location too far",
                "Rate too low",
                "Other",
              ].map((reason) => (
                <div key={reason} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="decline-reason"
                    id={reason}
                    value={reason}
                    checked={declineReason === reason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    className="h-4 w-4 text-gray-900 dark:text-foreground border-gray-300 dark:border-border focus:ring-gray-900 dark:focus:ring-foreground"
                  />
                  <Label
                    htmlFor={reason}
                    className="text-gray-600 dark:text-muted-foreground font-normal cursor-pointer"
                  >
                    {reason}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="font-bold text-gray-900 dark:text-foreground mb-3 block">
              Additional comments (optional)
            </h3>
            <Textarea
              placeholder="Any additional details.."
              className="min-h-[80px] bg-white dark:bg-background border-[#E8DDD3] dark:border-border text-gray-900 dark:text-foreground"
              value={declineComments}
              onChange={(e) => setDeclineComments(e.target.value)}
            />
          </div>

          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              className="flex-1 bg-gray-50 dark:bg-background border-none text-gray-900 dark:text-foreground hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={() => setDeclineShootEvent(null)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-[#F14A48] hover:bg-red-600 text-white gap-2"
              onClick={() => {
                if (declineShootEvent) {
                  // Pass decline reason and comments if needed?
                  // The API acceptOrDeclineProject current only takes crewAccept (2 for decline).
                  handleAcceptProject(
                    declineShootEvent.project_id || declineShootEvent.id,
                    false
                  );
                }
              }}
            >
              <CheckCircle2 className="h-4 w-4" /> Decline Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestsShootsPage;
